import { ExpandLess, ExpandMore, Visibility } from '@mui/icons-material';
import {
  Checkbox,
  Collapse,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { formatMoney } from 'lib/functions/utils';
import React, { useState } from 'react';

import {
  AccountingEntryClass,
  AccountingNode,
  CostCenter,
  EntryType,
} from '../../../../../types';
import EntryLinesDetail from '../../Table/components/EntryLinesDetail';
import { ProcessedEntry } from '../types';

interface ValidEntriesTableProps {
  entries: ProcessedEntry[];
  selectedEntries: string[];
  onSelectEntry: (entryId: string) => void;
  onSelectAll: () => void;
  onViewLines: (e: React.MouseEvent, entry: ProcessedEntry) => void;
  nodes?: AccountingNode[];
  costCenters?: CostCenter[];
  entryClasses: AccountingEntryClass[];
}

const ValidEntriesTable: React.FC<ValidEntriesTableProps> = ({
  entries,
  selectedEntries,
  onSelectEntry,
  onSelectAll,
  onViewLines,
  nodes,
  costCenters,
  entryClasses,
}) => {
  const [expandedEntries, setExpandedEntries] = useState<
    Record<string, boolean>
  >({});

  if (entries.length === 0) {
    return null;
  }

  const toggleEntry = (entryId: string) => {
    setExpandedEntries((prev) => ({
      ...prev,
      [entryId]: !prev[entryId],
    }));
  };

  const getEntryClass = (typeId: string | null) => {
    const entryClass = entryClasses.find((type) => String(type.id) === typeId);
    if (entryClass) {
      return entryClass.name;
    }
    return '-';
  };

  return (
    <Grid item xs={12}>
      <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" style={{ width: 48 }}>
                <Checkbox
                  indeterminate={
                    selectedEntries.length > 0 &&
                    selectedEntries.length < entries.length
                  }
                  checked={
                    entries.length > 0 &&
                    selectedEntries.length === entries.length
                  }
                  onChange={onSelectAll}
                />
              </TableCell>
              <TableCell style={{ width: 48 }}></TableCell>
              <TableCell style={{ width: 48 }}></TableCell>
              <TableCell>Nombre</TableCell>
              <TableCell>Clase</TableCell>
              <TableCell>Fecha</TableCell>
              <TableCell align="right">Líneas</TableCell>
              <TableCell align="right">Total Débito</TableCell>
              <TableCell align="right">Total Crédito</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {entries.map((processedEntry, index) => {
              const entryId = `entry-${index}`;
              const isSelected = selectedEntries.includes(entryId);
              const isExpanded = expandedEntries[entryId] || false;
              const totalDebit = processedEntry.entry.lines.reduce(
                (sum, line) => sum + line.debit,
                0,
              );
              const totalCredit = processedEntry.entry.lines.reduce(
                (sum, line) => sum + line.credit,
                0,
              );

              return (
                <React.Fragment key={entryId}>
                  <TableRow
                    hover
                    selected={isSelected}
                    onClick={() => onSelectEntry(entryId)}
                    sx={{
                      cursor: 'pointer',
                    }}
                  >
                    <TableCell
                      padding="checkbox"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Checkbox
                        checked={isSelected}
                        onChange={() => onSelectEntry(entryId)}
                      />
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleEntry(entryId);
                        }}
                        title={isExpanded ? 'Ocultar líneas' : 'Mostrar líneas'}
                      >
                        {isExpanded ? (
                          <ExpandLess fontSize="small" />
                        ) : (
                          <ExpandMore fontSize="small" />
                        )}
                      </IconButton>
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <IconButton
                        size="small"
                        onClick={(e) => onViewLines(e, processedEntry)}
                        title="Ver líneas"
                      >
                        <Visibility fontSize="small" />
                      </IconButton>
                    </TableCell>
                    <TableCell>{processedEntry.entry.name}</TableCell>
                    <TableCell>
                      {getEntryClass(processedEntry.entry.classId)}
                    </TableCell>
                    <TableCell>{processedEntry.entry.date}</TableCell>
                    <TableCell align="right">
                      {processedEntry.entry.lines.length}
                    </TableCell>
                    <TableCell align="right">
                      {formatMoney(totalDebit)}
                    </TableCell>
                    <TableCell align="right">
                      {formatMoney(totalCredit)}
                    </TableCell>
                  </TableRow>
                  {isExpanded && (
                    <TableRow>
                      <TableCell
                        colSpan={8}
                        style={{ padding: 0 }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <EntryLinesDetail
                            lines={processedEntry.entry.lines}
                            nodes={nodes}
                            costCenters={costCenters}
                          />
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Grid>
  );
};

export default ValidEntriesTable;
